import {
  Box,
  Button,
  GlobalStyles,
  IconButton,
  Skeleton,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Download, Trash2 } from "lucide-react";
import Gallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import React, { useState, useCallback } from "react";
import ConfirmationModal from "@/modules/common/components/modals/cofirmation-modal";

const GalleryImageWithSkeleton = ({
  image,
  thumb = false,
}: {
  image: string;
  thumb?: boolean;
}) => {
  const [loaded, setLoaded] = useState(false);
  const theme = useTheme();
  return (
    <>
      {!loaded && (
        <Skeleton
          sx={{
            height: 200,
            maxHeight: "100%",
            width: "100%",
          }}
        />
      )}
      <img
        src={image}
        alt="media"
        style={{
          width: "100%",
          borderRadius: thumb ? undefined : theme.nawy.radius.xl,
          display: loaded ? "block" : "none",
        }}
        onLoad={() => setLoaded(true)}
      />
    </>
  );
};

interface MediaGalleryProps {
  data: string[] | undefined;
  isLoading: boolean;
  onDownload?: (url: string, index: number) => void;
  onDelete?: (url: string, index: number) => void;
  isDownloading?: boolean;
}

export default function MediaGallery({
  data,
  isLoading,
  onDownload,
  onDelete,
  isDownloading = false,
}: Readonly<MediaGalleryProps>) {
  const theme = useTheme();
  const currentIndexRef = React.useRef(0);
  const galleryRef = React.useRef<any>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDownload = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      const currentIndex =
        galleryRef.current?.getCurrentIndex() ?? currentIndexRef.current;
      if (data && data[currentIndex] && onDownload) {
        onDownload(data[currentIndex], currentIndex);
      }
    },
    [data, onDownload]
  );

  const handleDeleteClick = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    const currentIndex =
      galleryRef.current?.getCurrentIndex() ?? currentIndexRef.current;
    if (data && data[currentIndex] && onDelete) {
      onDelete(data[currentIndex], currentIndex);
    }
    setShowDeleteModal(false);
  }, [data, onDelete]);

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleSlideChange = useCallback((index: number) => {
    currentIndexRef.current = index;
  }, []);

  const showActions = onDownload || onDelete;

  // Workaround for type definitions as defined in this issue: https://github.com/xiaolin/react-image-gallery/issues/801#issuecomment-2591763201
  const ImageGallery = (Gallery as any).default
    ? (Gallery as any).default
    : Gallery;

  const renderNavButton =
    ({ icon, sx }: { icon: React.ReactNode; sx?: SxProps }) =>
    (onClick: React.MouseEventHandler<HTMLButtonElement>, disabled: boolean) =>
      (
        <Button
          onClick={onClick}
          disabled={disabled}
          aria-label="Previous Slide"
          sx={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 5,
            color: theme.nawy.colors.primitives.grey[900],
            backgroundColor: theme.nawy.colors.primitives.white[100],
            borderRadius: theme.nawy.radius.full,
            minWidth: 0,
            p: theme.nawy.spacing.xxxs,
            m: 0,
            "&:hover": {
              color: theme.nawy.colors.primitives.white[100],
              backgroundColor: "transparent",
            },
            ...sx,
          }}
        >
          {icon}
        </Button>
      );

  if (isLoading)
    return (
      <Skeleton
        sx={{
          height: 200,
        }}
      />
    );

  if (!data || data.length === 0) {
    return (
      <Typography
        sx={{
          ...theme.nawy.typography.english.variants.body.md.regular,
        }}
      >
        The property has no media yet.
      </Typography>
    );
  }
  return (
    <>
      <GlobalStyles
        styles={{
          ".image-gallery-thumbnail": {
            minWidth: 40,
            minHeight: 40,
            maxHeight: 40,
            maxWidth: 40,
            overflow: "hidden",
            borderRadius: 6,
          },
          ".image-gallery-thumbnail img, .image-gallery-thumbnail-inner": {
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "6px",
          },
          ".image-gallery-thumbnail.active, .image-gallery-thumbnail:focus, .image-gallery-thumbnail:hover":
            {
              border: `1px solid ${theme.nawy.colors.semantic.border.accent}`,
              padding: 2,
            },
          ".image-gallery-thumbnails .image-gallery-thumbnails-container": {
            textAlign: "start",
            display: "flex",
            overflow: "auto",
          },
        }}
      />
      <ImageGallery
        ref={galleryRef}
        items={
          data?.map((item) => ({
            original: item,
            thumbnail: item,
            renderItem: () => <GalleryImageWithSkeleton image={item} />,
            renderThumbInner: () => (
              <GalleryImageWithSkeleton thumb image={item} />
            ),
          })) || []
        }
        showPlayButton={false}
        showFullscreenButton={false}
        onSlide={handleSlideChange}
        startIndex={currentIndexRef.current}
        renderLeftNav={renderNavButton({
          icon: <ChevronLeft />,
          sx: {
            left: "1%",
          },
        })}
        renderCustomControls={
          showActions
            ? () => (
                <Box
                  sx={{
                    position: "absolute",
                    top: theme.nawy.spacing.sm,
                    right: theme.nawy.spacing.sm,
                    display: "flex",
                    gap: theme.nawy.spacing.xxs,
                    zIndex: 5,
                  }}
                >
                  {onDownload && (
                    <IconButton
                      onClick={handleDownload}
                      disabled={isDownloading}
                      aria-label="Download image"
                      sx={{
                        color: theme.nawy.colors.primitives.grey[900],
                        backgroundColor:
                          theme.nawy.colors.primitives.white[100],
                        borderRadius: theme.nawy.radius.full,
                        p: theme.nawy.spacing.xxxs,
                        "&:hover": {
                          backgroundColor:
                            theme.nawy.colors.primitives.grey[100],
                        },
                      }}
                    >
                      <Download size={20} />
                    </IconButton>
                  )}
                  {onDelete && (
                    <IconButton
                      onClick={handleDeleteClick}
                      aria-label="Delete image"
                      sx={{
                        color: theme.nawy.colors.primitives.grey[900],
                        backgroundColor:
                          theme.nawy.colors.primitives.white[100],
                        borderRadius: theme.nawy.radius.full,
                        p: theme.nawy.spacing.xxxs,
                        "&:hover": {
                          backgroundColor: theme.palette.error.light,
                          color: theme.palette.error.main,
                        },
                      }}
                    >
                      <Trash2 size={20} />
                    </IconButton>
                  )}
                </Box>
              )
            : undefined
        }
        renderRightNav={renderNavButton({
          icon: <ChevronRight />,
          sx: {
            right: "1%",
          },
        })}
      />
      <ConfirmationModal
        open={showDeleteModal}
        setOpen={setShowDeleteModal}
        onConfirmHandler={handleDeleteConfirm}
        onCancelHandler={handleDeleteCancel}
        title="Delete Image"
        body="Are you sure you want to delete this image? This action cannot be undone."
      />
    </>
  );
}
